import { z } from 'zod';
import {
  PRICELIST_UNASSIGNED_COMPANY_FILTER,
  PricelistHistorySortBySchema,
  thenSortSchema,
  type PricelistHistoryFilters,
  type PricelistHistorySortBy,
} from '@erp/rpc';

/**
 * Pure state for the pricelist upload history — the URL search shape, the
 * per-user saved-filter shape, and the projections the view is built from.
 * The upload-history twin of `pricelistCaseListState`.
 */

/** Stable key for this table's saved-filter row in `core_user_table_filter_configs`. */
export const PRICELIST_HISTORY_TABLE_ID = 'im-pricelist-history';

/** `YYYY-MM-DD`, as a native date input gives it. */
const DateOnlyParamSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

/**
 * The route search params this view owns, all `ph`-prefixed so they cannot
 * collide with the sibling tabs' state on the same route.
 */
export const PricelistHistorySearchSchema = z.object({
  phSearch: z.string().max(200).optional(),
  /** A company id, or `unassigned` for files assigned to none. */
  phCompanyId: z
    .union([z.uuid(), z.literal(PRICELIST_UNASSIGNED_COMPANY_FILTER)])
    .optional(),
  phUploadedBy: z.array(z.uuid()).max(50).optional(),
  phUploadedFrom: DateOnlyParamSchema.optional(),
  phUploadedTo: DateOnlyParamSchema.optional(),
  phEffectiveFrom: DateOnlyParamSchema.optional(),
  phEffectiveTo: DateOnlyParamSchema.optional(),
  phErpUploadFrom: DateOnlyParamSchema.optional(),
  phErpUploadTo: DateOnlyParamSchema.optional(),
  /** True: only files on a price change; false: only files on none. */
  phOnCase: z.boolean().optional(),
  phSortBy: PricelistHistorySortBySchema.optional(),
  phSortDirection: z.enum(['asc', 'desc']).optional(),
  /** Tie-breaking sort levels after `phSortBy`; a stale one degrades to none. */
  phThenSort: thenSortSchema(PricelistHistorySortBySchema)
    .optional()
    .catch(undefined),
  phPage: z.coerce.number().int().min(1).optional(),
  phPageSize: z.coerce.number().int().min(1).max(200).optional(),
});
export type PricelistHistorySearch = z.infer<
  typeof PricelistHistorySearchSchema
>;

/** What is remembered per user: everything but the page itself. */
export const PricelistHistoryFilterPrefsSchema =
  PricelistHistorySearchSchema.omit({ phPage: true });
export type PricelistHistoryFilterPrefs = z.infer<
  typeof PricelistHistoryFilterPrefsSchema
>;

/**
 * The keys that are FILTERS — what "Clear filters" resets. The sort and the
 * page size are view state and survive a clear.
 */
export const CLEARABLE_HISTORY_FILTER_KEYS = [
  'phSearch',
  'phCompanyId',
  'phUploadedBy',
  'phUploadedFrom',
  'phUploadedTo',
  'phEffectiveFrom',
  'phEffectiveTo',
  'phErpUploadFrom',
  'phErpUploadTo',
  'phOnCase',
] as const satisfies readonly (keyof PricelistHistoryFilterPrefs)[];

export const PERSISTED_HISTORY_FILTER_KEYS = [
  ...CLEARABLE_HISTORY_FILTER_KEYS,
  'phSortBy',
  'phSortDirection',
  'phThenSort',
  'phPageSize',
] as const satisfies readonly (keyof PricelistHistoryFilterPrefs)[];

/** A new user lands on everything, newest upload first (the server default). */
export const DEFAULT_HISTORY_FILTER_PREFS: PricelistHistoryFilterPrefs = {};

export const DEFAULT_HISTORY_PAGE_SIZE = 20;

function isEmpty(value: unknown): boolean {
  return (
    value === undefined ||
    value === '' ||
    (Array.isArray(value) && value.length === 0)
  );
}

/**
 * The persisted projection in a fixed key order with empties dropped, so
 * `JSON.stringify` is a reliable equality check on the saved row.
 */
export function buildHistoryFilterPrefs(
  params: Partial<PricelistHistoryFilterPrefs>,
): PricelistHistoryFilterPrefs {
  const prefs: Record<string, unknown> = {};
  for (const key of PERSISTED_HISTORY_FILTER_KEYS) {
    if (!isEmpty(params[key])) prefs[key] = params[key];
  }
  return PricelistHistoryFilterPrefsSchema.parse(prefs);
}

/** The search params as the contract's filter shape. */
export function toHistoryFilters(
  params: Partial<PricelistHistorySearch>,
): PricelistHistoryFilters {
  return {
    search: params.phSearch || undefined,
    companyId: params.phCompanyId,
    uploadedBy: params.phUploadedBy?.length ? params.phUploadedBy : undefined,
    uploadedFrom: params.phUploadedFrom,
    uploadedTo: params.phUploadedTo,
    effectiveFrom: params.phEffectiveFrom,
    effectiveTo: params.phEffectiveTo,
    erpUploadFrom: params.phErpUploadFrom,
    erpUploadTo: params.phErpUploadTo,
    onCase: params.phOnCase,
  };
}

/** True when any clearable filter is set. */
export function hasHistoryFilters(
  params: Partial<PricelistHistorySearch>,
): boolean {
  return CLEARABLE_HISTORY_FILTER_KEYS.some((key) => !isEmpty(params[key]));
}

/** Column ids that round-trip to the contract's `sortBy`; anything else is unsortable. */
export function toHistorySortBy(
  id: string | undefined,
): PricelistHistorySortBy | undefined {
  const parsed = PricelistHistorySortBySchema.safeParse(id);
  return parsed.success ? parsed.data : undefined;
}
